import { currentLocale } from '@/lib/i18n';
import { grantState, sessionFromGrant, type OfflineSession } from '@/lib/offline/grant';
import {
  getOfflineSnapshot,
  getOfflineStore,
  setOnlineBranch,
  setUnlockedUser,
  syncIfStale,
} from '@/lib/offline/runtime';
import type { PdaSession } from './session';

/**
 * Why the app cannot open without the server (fuel-pda.md 13.4): the PIN is
 * needed first, the 24-hour window ended, or this device has no grant.
 */
export type OfflineGateReason = 'LOCKED' | 'EXPIRED' | 'NO_GRANT';

export class OfflineGateError extends Error {
  readonly reason: OfflineGateReason;
  /** The grant's user, for the PIN screen. */
  readonly userId: string | null;

  constructor(reason: OfflineGateReason, userId: string | null = null) {
    super(`Offline start refused: ${reason}`);
    this.name = 'OfflineGateError';
    this.reason = reason;
    this.userId = userId;
  }
}

export function isOfflineGate(error: unknown): error is OfflineGateError {
  return error instanceof OfflineGateError;
}

/**
 * The session while the server cannot be reached: built from the cached grant
 * once the PIN unlocked it in this run (or the user was signed in online in
 * this run). Otherwise the reason the app must stop at the gate.
 */
export async function offlineSession(original: unknown): Promise<OfflineSession> {
  const store = await getOfflineStore();
  if (!store) throw original;
  const grant = await store.getValue('grant');
  const state = grantState(grant, Date.now(), await store.getValue('serverTime'));
  if (!grant || state === 'NONE') throw new OfflineGateError('NO_GRANT');
  if (state === 'EXPIRED') throw new OfflineGateError('EXPIRED', grant.userId);
  if (getOfflineSnapshot().unlockedUserId !== grant.userId) {
    const pin = await store.getPin(grant.userId);
    throw new OfflineGateError(pin ? 'LOCKED' : 'NO_GRANT', grant.userId);
  }
  const [lastSession, context] = await Promise.all([
    store.getValue('session'),
    store.getValue('context'),
  ]);
  return sessionFromGrant(grant, {
    locale: currentLocale(),
    now: Date.now(),
    lastSession,
    context,
  });
}

/**
 * An online session unlocks offline work for its user in this run (a drop
 * mid-shift needs no PIN), is kept for the branch name offline, and starts a
 * pull for the grant, the lists and the keys.
 */
export async function rememberOnlineSession(session: PdaSession): Promise<void> {
  setUnlockedUser(session.user.id);
  setOnlineBranch(session.branch.id);
  const store = await getOfflineStore();
  if (!store) return;
  await store.setValue('session', session);
  void syncIfStale();
}

/**
 * Signing out ends offline work for this user on the device: the grant and
 * the kept session go, the waiting operations stay and are sent later
 * (section 13.7, D11).
 */
export async function forgetOfflineAccess(): Promise<void> {
  setUnlockedUser(null);
  setOnlineBranch(null);
  const store = await getOfflineStore();
  if (!store) return;
  await store.deleteValue('grant');
  await store.deleteValue('session');
}
